import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Hexagon, Plus, Copy, CopyPlus, Pencil, Trash2, ExternalLink, LogOut, X, Loader2, Upload, LayoutGrid, Users, Presentation, Mail, Phone, Building2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DemoRenderer, TEMPLATES, templateById } from "@/components/demo/templates";
import MeetingsView from "@/components/admin/MeetingsView";

const in15Days = () => {
  const d = new Date();
  d.setDate(d.getDate() + 15);
  return d.toISOString().slice(0, 10);
};

const emptyForm = {
  template: "consultorio",
  business_name: "",
  slug: "",
  tagline: "",
  description: "",
  primary_color: "#0E7C7B",
  accent_color: "#14B8A6",
  phone: "",
  whatsapp: "",
  email: "",
  hero_image: "",
  about_image: "",
  show_erp: false,
  show_crm: false,
  expires_at: in15Days(),
  max_opens: 0,
  services: [],
};

const slugify = (s) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const expiryInfo = (d) => {
  if (!d.expires_at) return null;
  const days = Math.ceil((new Date(`${d.expires_at}T23:59:59`) - Date.now()) / 86400000);
  if (days < 0) return { text: "Expirada", cls: "border-red-500/40 text-red-400" };
  if (days <= 3) return { text: days === 0 ? "Expira hoy" : `Expira en ${days}d`, cls: "border-amber/40 text-amber" };
  return { text: `Expira: ${new Date(`${d.expires_at}T12:00:00`).toLocaleDateString("es-EC")}`, cls: "border-cy/15 text-slate-500" };
};

const inputCls =
  "w-full rounded-lg border border-cy/15 bg-qcard px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors focus:border-cy/60";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500";

const SERVICE_TITLES = {
  "software-a-medida": "Software a medida",
  "erp-crm": "ERP & CRM",
  "web-apps-pwa": "Aplicaciones Web & PWA",
  "desarrollo-web": "Desarrollo Web",
  "integracion-adaptacion": "Integración & Adaptación",
  "software-empresarial": "Software Empresarial",
};

const QuotesView = ({ quotes }) => {
  if (!quotes.length) {
    return (
      <div className="mt-10 rounded-xl border border-dashed border-cy/20 p-12 text-center text-sm text-slate-500" data-testid="quotes-empty">
        Aún no hay solicitudes de cotización. Cuando alguien envíe el formulario de la página, aparecerá aquí.
      </div>
    );
  }
  const groups = {};
  quotes.forEach((q) => {
    const key = q.service || "otros";
    (groups[key] = groups[key] || []).push(q);
  });
  return (
    <div className="mt-8 grid gap-8" data-testid="quotes-list">
      {Object.entries(groups).map(([svc, items]) => (
        <div key={svc} data-testid={`quotes-group-${svc}`}>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-lg font-bold text-slate-100">{SERVICE_TITLES[svc] || svc}</h3>
            <span className="rounded-full border border-cy/30 bg-cy/10 px-3 py-0.5 font-mono text-[11px] text-cy">{items.length}</span>
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            {items.map((q) => (
              <div key={q.id} className="rounded-xl border border-cy/10 bg-qcard p-5 transition-colors hover:border-cy/30" data-testid={`quote-item-${q.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-display text-base font-bold text-slate-100">{q.name}</p>
                  <span className="shrink-0 font-mono text-[10px] text-slate-500">
                    {new Date(q.created_at).toLocaleDateString("es-EC")}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-cy" />{q.company}</span>
                  <span className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-cy" />{q.email}</span>
                  {q.phone && <span className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-cy" />{q.phone}</span>}
                </div>
                <p className="mt-3 rounded-lg bg-ink/50 p-3 text-sm leading-relaxed text-slate-300">{q.message}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [demos, setDemos] = useState([]);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [tab, setTab] = useState("demos");
  const [quotes, setQuotes] = useState([]);

  const loadDemos = () => api.get("/demos").then((r) => setDemos(r.data));
  const loadQuotes = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadDemos();
        loadQuotes();
      })
      .catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setSlugTouched(false);
    setEditor({ mode: "create", form: { ...emptyForm } });
  };

  const openEdit = (demo) => {
    setSlugTouched(true);
    setEditor({ mode: "edit", id: demo.id, form: { ...emptyForm, ...demo } });
  };

  const setField = (k, v) => {
    setEditor((e) => {
      const form = { ...e.form, [k]: v };
      if (k === "business_name" && !slugTouched) form.slug = slugify(v);
      if (k === "template") {
        const t = templateById(v);
        form.primary_color = t.primary;
        form.accent_color = t.accent;
        if (!form.services.length) form.services = [...t.defaultServices];
      }
      return { ...e, form };
    });
    if (k === "slug") setSlugTouched(true);
  };

  const setService = (i, k, v) =>
    setEditor((e) => {
      const services = e.form.services.map((s, j) => (j === i ? { ...s, [k]: v } : s));
      return { ...e, form: { ...e.form, services } };
    });

  const uploadImage = async (file, field) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(field);
    try {
      const r = await api.post("/demos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(field, r.data.url);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await api.post("/demos", editor.form);
        toast.success("Demo creada. Copia el enlace y compártelo con tu cliente.");
      } else {
        await api.put(`/demos/${editor.id}`, editor.form);
        toast.success("Demo actualizada");
      }
      setEditor(null);
      loadDemos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (demo) => {
    const { id, created_at, updated_at, opens_count, ...rest } = demo;
    try {
      await api.post("/demos", { ...rest, business_name: `${demo.business_name} (copia)`, slug: `${demo.slug}-copia` });
      toast.success("Demo duplicada como copia editable");
      loadDemos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (demo) => {
    if (!window.confirm(`¿Eliminar la demo de "${demo.business_name}"? El enlace dejará de funcionar.`)) return;
    try {
      await api.delete(`/demos/${demo.id}`);
      toast.success("Demo eliminada");
      loadDemos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/demo/${slug}`);
    toast.success("Enlace copiado al portapapeles");
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="animate-pulse font-mono text-xs tracking-[0.3em] text-cy">VERIFICANDO ACCESO…</p>
      </div>
    );
  }

  const ImageField = ({ label, field, testid }) => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2">
        <input data-testid={testid} value={editor.form[field]} onChange={(e) => setField(field, e.target.value)} placeholder="https://... o sube una foto" className={inputCls} />
        <label
          data-testid={`${testid}-upload`}
          title="Subir imagen desde tu computadora"
          className={`grid h-10 w-11 shrink-0 cursor-pointer place-items-center rounded-lg border border-cy/25 text-cy transition-colors hover:bg-cy/10 ${uploading === field ? "pointer-events-none opacity-60" : ""}`}
        >
          {uploading === field ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], field)} />
        </label>
      </div>
      {editor.form[field] && (
        <img src={editor.form[field]} alt="" className="mt-2 h-16 w-24 rounded-md border border-cy/15 object-cover" />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-ink font-body text-slate-200" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-cy/10 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="relative grid h-9 w-9 place-items-center">
              <Hexagon className="h-9 w-9 text-cy" strokeWidth={1.4} />
              <span className="absolute font-display text-xs font-extrabold text-ink">Q</span>
            </span>
            <div className="leading-none">
              <p className="font-display text-sm font-bold tracking-[0.18em] text-slate-100">PANEL QC</p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">{user.email}</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-cy/20 px-4 py-2 font-mono text-xs text-slate-400 transition-colors hover:border-cy hover:text-cy"
          >
            <LogOut className="h-3.5 w-3.5" /> Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cy">// Ambiente de pruebas</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-slate-50">Demos para clientes</h1>
            <p className="mt-2 max-w-xl text-sm text-slate-400">
              Crea una página de muestra personalizada por cliente y comparte el enlace privado. El cliente verá cómo quedaría su página.
            </p>
          </div>
          {tab === "demos" && (
          <button
            data-testid="admin-new-demo-btn"
            onClick={openCreate}
            className="flex items-center gap-2 rounded-full bg-cy px-6 py-3 font-display text-sm font-bold text-ink transition-all hover:bg-em hover:shadow-[0_0_25px_rgba(0,240,255,0.35)]"
          >
            <Plus className="h-4 w-4" /> Nueva demo
          </button>
          )}
        </div>

        <div className="mt-8 flex gap-2 border-b border-cy/10" data-testid="admin-tabs">
          {[
            { id: "demos", label: `Demos (${demos.length})` },
            { id: "quotes", label: `Cotizaciones (${quotes.length})` },
            { id: "meetings", label: "Reuniones" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`border-b-2 px-4 py-2.5 font-display text-sm font-bold transition-colors ${
                tab === t.id ? "border-cy text-cy" : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "quotes" && <QuotesView quotes={quotes} />}

        {tab === "meetings" && <MeetingsView />}

        {tab === "demos" && (
        <div className="mt-10 grid gap-4" data-testid="demo-list">
          {demos.length === 0 && (
            <div className="rounded-xl border border-dashed border-cy/20 p-12 text-center text-sm text-slate-500" data-testid="demo-list-empty">
              Aún no hay demos. Crea la primera con el botón "Nueva demo".
            </div>
          )}
          {demos.map((d) => {
            const ex = expiryInfo(d);
            return (
              <div
                key={d.id}
                data-testid={`demo-item-${d.slug}`}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-cy/10 bg-qcard p-5 transition-colors hover:border-cy/30"
              >
                <span className="h-10 w-10 shrink-0 rounded-lg" style={{ background: `linear-gradient(135deg, ${d.primary_color}, ${d.accent_color})` }} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-display text-lg font-bold text-slate-100">{d.business_name}</p>
                    {ex && (
                      <span data-testid={`demo-expiry-${d.slug}`} className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] ${ex.cls}`}>
                        {ex.text}
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-xs text-slate-500">
                    {templateById(d.template).name} · /demo/{d.slug} · {new Date(d.created_at).toLocaleDateString("es-EC")} · Aperturas: {d.opens_count || 0}
                    {d.max_opens ? `/${d.max_opens}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a data-testid={`demo-present-${d.slug}`} href={`/demo/${d.slug}?present=1`} target="_blank" rel="noopener noreferrer" title="Modo presentación (pantalla limpia)" className="grid h-9 w-9 place-items-center rounded-lg border border-em/30 text-em/80 transition-colors hover:border-em hover:text-em">
                    <Presentation className="h-4 w-4" />
                  </a>
                  <button data-testid={`demo-duplicate-${d.id}`} onClick={() => duplicate(d)} title="Duplicar demo" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy">
                    <CopyPlus className="h-4 w-4" />
                  </button>
                  <button data-testid={`demo-copy-${d.slug}`} onClick={() => copyLink(d.slug)} title="Copiar enlace" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy">
                    <Copy className="h-4 w-4" />
                  </button>
                  <a data-testid={`demo-view-${d.slug}`} href={`/demo/${d.slug}`} target="_blank" rel="noopener noreferrer" title="Ver demo" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button data-testid={`demo-edit-${d.id}`} onClick={() => openEdit(d)} title="Editar" className="grid h-9 w-9 place-items-center rounded-lg border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`demo-delete-${d.id}`} onClick={() => remove(d)} title="Eliminar" className="grid h-9 w-9 place-items-center rounded-lg border border-red-500/30 text-red-400/80 transition-colors hover:border-red-400 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </main>

      {editor && (
        <div className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-md" data-testid="demo-editor">
          <div className="grid h-full lg:grid-cols-2">
            <div className="overflow-y-auto border-r border-cy/10 bg-panel p-6 sm:p-10">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-slate-50">
                  {editor.mode === "create" ? "Nueva demo" : `Editar: ${editor.form.business_name}`}
                </h2>
                <button data-testid="demo-form-cancel" onClick={() => setEditor(null)} className="grid h-9 w-9 place-items-center rounded-full border border-cy/20 text-slate-400 hover:border-cy hover:text-cy">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-8">
                <span className={labelCls}>Plantilla base</span>
                <div className="grid gap-3 sm:grid-cols-3">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`template-select-${t.id}`}
                      onClick={() => setField("template", t.id)}
                      className={`rounded-lg border p-3 text-left transition-all ${editor.form.template === t.id ? "border-cy/60 bg-cy/10" : "border-cy/10 bg-qcard hover:border-cy/30"}`}
                    >
                      <span className="mb-2 block h-6 w-6 rounded" style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})` }} />
                      <p className="text-xs font-bold text-slate-200">{t.name}</p>
                      <p className="mt-1 text-[10px] leading-snug text-slate-500">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Nombre del negocio *</label>
                  <input data-testid="demo-form-business-name" value={editor.form.business_name} onChange={(e) => setField("business_name", e.target.value)} placeholder="Ej. Consultorio Dra. Paz" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Enlace (slug) *</label>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500">/demo/</span>
                    <input data-testid="demo-form-slug" value={editor.form.slug} onChange={(e) => setField("slug", slugify(e.target.value))} placeholder="consultorio-dra-paz" className={inputCls} />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Eslogan / frase principal</label>
                  <input data-testid="demo-form-tagline" value={editor.form.tagline} onChange={(e) => setField("tagline", e.target.value)} placeholder="Ej. Tu salud en las mejores manos" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Descripción</label>
                  <textarea data-testid="demo-form-description" rows={3} value={editor.form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Breve presentación del negocio..." className={`${inputCls} resize-none`} />
                </div>
                <div>
                  <label className={labelCls}>Color principal</label>
                  <div className="flex items-center gap-2">
                    <input data-testid="demo-form-primary-color" type="color" value={editor.form.primary_color} onChange={(e) => setField("primary_color", e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-cy/15 bg-qcard" />
                    <span className="font-mono text-xs text-slate-500">{editor.form.primary_color}</span>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Color de acento</label>
                  <div className="flex items-center gap-2">
                    <input data-testid="demo-form-accent-color" type="color" value={editor.form.accent_color} onChange={(e) => setField("accent_color", e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-cy/15 bg-qcard" />
                    <span className="font-mono text-xs text-slate-500">{editor.form.accent_color}</span>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Teléfono del cliente</label>
                  <input data-testid="demo-form-phone" value={editor.form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+593 2 000 0000" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>WhatsApp del cliente (con código de país)</label>
                  <input data-testid="demo-form-whatsapp" value={editor.form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value)} placeholder="593991234567" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Correo del cliente</label>
                  <input data-testid="demo-form-email" value={editor.form.email} onChange={(e) => setField("email", e.target.value)} placeholder="contacto@negocio.com" className={inputCls} />
                </div>
                <ImageField label="Imagen principal" field="hero_image" testid="demo-form-hero-image" />
                <ImageField label="Imagen secundaria" field="about_image" testid="demo-form-about-image" />

                <div className="sm:col-span-2">
                  <span className={labelCls}>Vista de sistema (muestra los módulos con datos de ejemplo, no funcional)</span>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { k: "show_erp", icon: LayoutGrid, name: "Incluir vista ERP", desc: "Pedidos, stock, compras, agenda, facturación…" },
                      { k: "show_crm", icon: Users, name: "Incluir vista CRM", desc: "Clientes, seguimiento, embudo de ventas…" },
                    ].map((opt) => (
                      <button
                        key={opt.k}
                        type="button"
                        data-testid={`demo-form-${opt.k.replace("_", "-")}`}
                        onClick={() => setField(opt.k, !editor.form[opt.k])}
                        className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-all ${editor.form[opt.k] ? "border-cy/60 bg-cy/10" : "border-cy/10 bg-qcard hover:border-cy/30"}`}
                      >
                        <opt.icon className={`mt-0.5 h-5 w-5 shrink-0 ${editor.form[opt.k] ? "text-cy" : "text-slate-500"}`} />
                        <span>
                          <span className={`block text-sm font-bold ${editor.form[opt.k] ? "text-slate-100" : "text-slate-300"}`}>{opt.name}</span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{opt.desc}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className={labelCls}>Vencimiento del enlace</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      data-testid="demo-form-expires-at"
                      type="date"
                      value={editor.form.expires_at}
                      onChange={(e) => setField("expires_at", e.target.value)}
                      className={`${inputCls} max-w-[220px] [color-scheme:dark]`}
                    />
                    <button
                      type="button"
                      data-testid="demo-form-no-expiry"
                      onClick={() => setField("expires_at", "")}
                      className="rounded-full border border-cy/20 px-4 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:border-cy hover:text-cy"
                    >
                      Sin vencimiento
                    </button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Pasada esta fecha el cliente verá "demo expirada". Por defecto: 15 días desde hoy.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className={labelCls}>Máximo de aperturas del enlace</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      data-testid="demo-form-max-opens"
                      type="number"
                      min="0"
                      value={editor.form.max_opens}
                      onChange={(e) => setField("max_opens", Math.max(0, parseInt(e.target.value, 10) || 0))}
                      className={`${inputCls} max-w-[140px]`}
                    />
                    <span className="font-mono text-[11px] text-slate-500">0 = ilimitado</span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Al llegar al límite, el enlace deja de abrir. Así la propuesta no circula de más ni llega a otro proveedor.
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <span className={labelCls}>Servicios / productos</span>
                  <button
                    data-testid="demo-service-add"
                    onClick={() => setEditor((e) => ({ ...e, form: { ...e.form, services: [...e.form.services, { title: "", description: "" }] } }))}
                    className="flex items-center gap-1.5 rounded-full border border-cy/25 px-3 py-1.5 font-mono text-[11px] text-cy transition-colors hover:bg-cy/10"
                  >
                    <Plus className="h-3 w-3" /> Agregar
                  </button>
                </div>
                <div className="mt-3 grid gap-3">
                  {editor.form.services.map((s, i) => (
                    <div key={i} className="flex items-start gap-2" data-testid={`demo-service-row-${i}`}>
                      <div className="grid flex-1 gap-2">
                        <input value={s.title} onChange={(e) => setService(i, "title", e.target.value)} placeholder={`Servicio ${i + 1}`} className={inputCls} data-testid={`demo-service-title-${i}`} />
                        <input value={s.description} onChange={(e) => setService(i, "description", e.target.value)} placeholder="Descripción corta (opcional)" className={inputCls} />
                      </div>
                      <button
                        onClick={() => setEditor((e) => ({ ...e, form: { ...e.form, services: e.form.services.filter((_, j) => j !== i) } }))}
                        className="mt-2 text-slate-600 transition-colors hover:text-red-400"
                        data-testid={`demo-service-remove-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                data-testid="demo-form-save"
                onClick={save}
                disabled={saving || !editor.form.business_name || !editor.form.slug}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-cy px-6 py-3.5 font-display text-sm font-bold text-ink transition-all hover:bg-em disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? "Guardando..." : editor.mode === "create" ? "Crear demo" : "Guardar cambios"}
              </button>
            </div>

            <div className="relative hidden overflow-hidden bg-[#0C1017] lg:block">
              <p className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-full border border-cy/20 bg-ink/80 px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] text-cy backdrop-blur">
                VISTA PREVIA EN VIVO
              </p>
              <div className="h-full w-full overflow-y-auto p-6">
                <div className="overflow-hidden rounded-xl border border-cy/20 shadow-2xl">
                  <div style={{ transform: "scale(0.62)", transformOrigin: "top left", width: "161%", pointerEvents: "none" }}>
                    <DemoRenderer demo={{ ...editor.form, services: editor.form.services.filter((s) => s.title) }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
